import { storeToRefs } from 'pinia'
import { computed, onScopeDispose, watch, type ComputedRef, type Ref } from 'vue'

import type {
  WorkflowCategory,
  WorkflowDefinition,
  WorkflowLoadStatus,
  WorkflowScope,
} from '../models/workflow.types'
import { useWorkflowStore } from '../stores/workflowStore'

import { useWorkflowRbac } from './useWorkflowRbac'

import { useAccount, useWorkspace } from '@/features/account'
import type { ApiError } from '@/shared/http'

export interface UseWorkflow {
  definitions: Ref<WorkflowDefinition[]>
  categories: ComputedRef<WorkflowCategory[]>
  status: Ref<WorkflowLoadStatus>
  error: Ref<ApiError | null>
  reload: () => Promise<void>
  getById: (id: number | string) => WorkflowDefinition | null
}

export function useWorkflow(): UseWorkflow {
  const account = useAccount()
  const workspace = useWorkspace()
  const access = useWorkflowRbac()
  const store = useWorkflowStore()
  const { definitions, status, error } = storeToRefs(store)
  let controller: AbortController | null = null
  const scope = computed<WorkflowScope | null>(() => {
    if (!access.canAccess.value) return null
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    return accountId === undefined || workspaceId === undefined ? null : { accountId, workspaceId }
  })

  async function load(reload: boolean): Promise<void> {
    controller?.abort()
    if (!scope.value) {
      store.reset()
      return
    }
    const activeController = new AbortController()
    controller = activeController
    await store.fetchDefinitions(scope.value, reload, activeController.signal)
    if (controller === activeController) controller = null
  }

  async function reload(): Promise<void> {
    await load(true)
  }

  watch(
    () =>
      scope.value
        ? `${scope.value.accountId}:${scope.value.workspaceId}`
        : `denied:${access.canAccess.value}`,
    () => {
      store.reset()
      if (scope.value) void load(false)
    },
    { immediate: true },
  )
  onScopeDispose(() => {
    controller?.abort()
    controller = null
    store.reset()
  })

  return {
    definitions,
    categories: computed(() => store.categories),
    status,
    error,
    reload,
    getById: (id) => store.getById(id),
  }
}
